import type { IConnectedBimSpace } from "@schemas/issueSchema";
import React, { useMemo } from "react";
import { openapi, usePutProjectByProjectIdIssueConnectedBimSpacesMutation } from "../../../../../../openapi";
import ConnectExistingIssueToItemModalWrapper from "../../../../ConnectExistingIssueToItemModalWrapper";
import { useAppDispatch } from "../../../../../hooks";
import { thunkOpenIssueModalById } from "../../issueSlice";

const getSpaceKey = (space: IConnectedBimSpace): string => {
  const spaceDbId = String(space.spaceDbId ?? "").trim();

  if (spaceDbId !== "") {
    return `id:${spaceDbId}`;
  }

  return [space.floor, space.roomNumber]
    .map((value) => String(value ?? "").trim().toLowerCase())
    .join("|");
};

const mergeConnectedBimSpaces = (
  existingSpaces: IConnectedBimSpace[] = [],
  spacesToConnect: IConnectedBimSpace[],
): IConnectedBimSpace[] => {
  const spacesByKey = new Map<string, IConnectedBimSpace>();

  for (const space of existingSpaces) {
    spacesByKey.set(getSpaceKey(space), space);
  }

  for (const space of spacesToConnect) {
    spacesByKey.set(getSpaceKey(space), space);
  }

  return Array.from(spacesByKey.values());
};

const getConnectedBimSpacesPayload = (spaces: IConnectedBimSpace[]) =>
  spaces.map((space) => ({
    ...space,
    roomNumber: space.roomNumber ?? "",
  }));

const FloorplanConnectIssueModal: React.FC<{
  opened: boolean;
  projectId: string;
  issueBoardId?: string;
  connectedBimSpaces: IConnectedBimSpace[];
  blockedIssueIds?: string[];
  onClose: () => void;
  onConnected?: () => void;
}> = ({ opened, projectId, issueBoardId, connectedBimSpaces, blockedIssueIds = [], onClose, onConnected }) => {
  const dispatch = useAppDispatch();
  const [updateConnectedBimSpaces] = usePutProjectByProjectIdIssueConnectedBimSpacesMutation();

  const defaultIssueBoard = useMemo(
    () => (issueBoardId != null ? { _id: issueBoardId, name: "" } : undefined),
    [issueBoardId],
  );
  const allowedIssueBoardIds = useMemo(() => (issueBoardId != null ? [issueBoardId] : []), [issueBoardId]);

  return (
    <ConnectExistingIssueToItemModalWrapper
      opened={opened}
      projectId={projectId}
      defaultIssueBoard={defaultIssueBoard}
      allowedIssueBoardIds={allowedIssueBoardIds}
      blockedIssueIds={blockedIssueIds}
      hideBoardSelector
      closeModal={onClose}
      allowedTypes={["ISSUE"]}
      modalTitle="Koble sak"
      onConnected={async (_boardId, issueId) => {
        const issue = await dispatch(
          openapi.endpoints.getProjectByProjectIdIssuesGetSingle.initiate(
            {
              projectId,
              issueId,
            },
            { forceRefetch: true },
          ),
        ).unwrap();

        await updateConnectedBimSpaces({
          projectId,
          body: {
            issueId,
            connectedBimSpaces: getConnectedBimSpacesPayload(
              mergeConnectedBimSpaces(issue.connectedBimSpaces ?? [], connectedBimSpaces),
            ),
          },
        }).unwrap();

        onClose();
        onConnected?.();
        dispatch(thunkOpenIssueModalById(issueId));
      }}
    />
  );
};

export default FloorplanConnectIssueModal;
